import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Text,
  Spinner,
  FormHelperText,
  Heading,
  Divider,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Checkbox,
} from "@chakra-ui/react";
import { Fragment, useEffect, useState } from "react";
import { FaPlay, FaEnvelope } from "react-icons/fa";
import { componentState } from "../../../atoms/componentState";
import TitleFlex from "./components/titleFlex";
import { NumericFormat, PatternFormat } from "react-number-format";
import { stateOptions } from "../../../constants/stateOptions";
import moment from "moment";
import "moment/locale/pt-br";
import { dateForAPI, dateForValueInput } from "../../../constants/dataFormat";
import { cnpj as cnpjValidator, cpf as cpfValidator } from "cpf-cnpj-validator";
import { JWTToken } from "../../../routes/privateRoute";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { goToVendaBolePixListagem } from "../../../routes/coordinator";
import { customColors } from "../../../atoms/customColors";
import { useSetRecoilState, useRecoilState } from "recoil";
import ModalRevisãoVendaBoletoLote from "./components/ModalRevisãoVendaBoletoLote";
import VendasBoletoLoteApi from "../../../services/VendasBoletoloteApi";
import axios from "axios";

/**
 * Extrai a mensagem de erro mais útil de uma falha de request, cobrindo os
 * formatos que a cadeia web/core pode retornar:
 *  - objeto { _error: "..." }         (erro de negócio do core)
 *  - objeto { message: "..." }
 *  - array  ["campo: msg", ...]        (validação de ModelState)
 *  - string crua
 * Cai para error.message em vez de despejar o objeto inteiro na tela.
 */
export function extrairMensagemErro(error: any): string {
  const data = error?.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) return data;
    if (data._error) return data._error;
    if (data.message) return data.message;
    if (Array.isArray(data) && data.length) return data.join(" | ");
  }
  return error?.message || "Erro inesperado. Tente novamente.";
}

export interface VendaBoleto {
  idCliente: number;
  valor: number;
  valorPrimeiraEmissao: number;
  vencimento: string;
  diaVencimento: number;
  descricao: string;
  cpfPagador: string;
  nomePagador: string;
  emailPagador: string;
  enderRua: string;
  enderBairro: string;
  enderCEP: string;
  enderCidade: string;
  enderComplemento: string;
  enderEstado: string;
  enderNumero: string;
  enderReferencia: string;
  permitirRegras: boolean;
  isCancelado: boolean;
  mesesPular: number;
  dataPrimeiraEmissao: string;
  numeroPagador: string;
  isAvulso: boolean;
  dataEmissao?: string;
  enviarEmail: boolean;
  enviarSMS: boolean;
  enviarWhats: boolean;
  RegrasJuros: {
    mode: string;
    frequency: string | null;
    value: number;
    start_at: string;
  } | null;
  RegrasMulta: {
    mode: string;
    value: number;
    start_at: string;
  };
  RegrasDesconto: {
    mode: string;
    value: number;
    diasAntesVencimento: number;
  };
  descontoNaPrimeiraParcela: boolean;
  dataLimitePagamento?: string;
}

function toCentsExact(value: any) {
  if (value == null) throw new Error("Input value cannot be null or undefined");
  let valueStr = value.toString().replace(",", ".");
  const num = parseFloat(valueStr);
  if (isNaN(num)) throw new Error("Invalid number format");
  return Math.round(num * 100);
}

const getInterestConfig = (currentMode: string) => {
  switch (currentMode) {
    case "VALOR_DIARIO":
      return { mode: "FIXED", frequency: "DAILY", isPercentage: false };
    case "PERCENTUAL_DIARIO":
      return { mode: "PERCENTAGE", frequency: "DAILY", isPercentage: true };
    case "PERCENTUAL_MENSAL":
      return { mode: "PERCENTAGE", frequency: "MONTHLY", isPercentage: true };
    case "SEM_JUROS":
    default:
      return { mode: "SEM_JUROS", frequency: null, isPercentage: false };
  }
};

const getMultaConfig = (currentMode: string) => {
  switch (currentMode) {
    case "VALOR_FIXO":
      return { mode: "FIXED", isPercentage: false };
    case "PERCENTUAL":
      return { mode: "PERCENTAGE", isPercentage: true };
    case "SEM_MULTA":
    default:
      return { mode: "SEM_MULTA", isPercentage: false };
  }
};

// Presets de densidade da tela: rótulos/inputs menores para caber mais campo
// por linha sem perder legibilidade.
// Piso operacional do juros (BACEN / banco): R$ 0,01 por dia, R$ 0,30 por mês.
// Ele cruza com o teto legal de 1% a.m. em R$ 30,00 (1% de 30 = 0,30): abaixo
// disso não existe juros que respeite os dois — qualquer valor ou fica abaixo
// do mínimo que o banco cobra, ou passa do limite legal.
const PISO_JUROS_DIA = 0.01;
const PISO_JUROS_MES = 0.3;
const VALOR_MINIMO_COM_JUROS = 30;

const labelSm = { fontSize: "sm", mb: 1 } as const;
const ajudaSm = { fontSize: "xs", mt: 1 } as const;
const campoSm = { size: "sm" } as const;

const convertValueToCentsString = (
  value: number,
  isPercentage: boolean
): string => {
  if (isPercentage) {
    return Math.round(value * 100).toString();
  } else {
    return toCentsExact(value).toString();
  }
};

export default function VendasBolePixLote() {
  const setHeaderLocation = useSetRecoilState(componentState);
  useEffect(() => setHeaderLocation("Venda no Boleto em Lote"), []);
  const navigate = useNavigate();
  const colors = useRecoilState(customColors)[0];
  const token = window.localStorage.getItem("token");
  let jwtToken: JWTToken = jwt_decode(token || "");
  const cliente = Number(jwtToken.idCliente);
  const idLogin = Number(jwtToken.primarysid);
  const unMask = (value: any) => value.replace(/[^0-9]/g, "");

  const initialValues: VendaBoleto = {
    idCliente: cliente,
    valor: 0,
    valorPrimeiraEmissao: 0,
    vencimento: moment().format(dateForAPI),
    diaVencimento: 1,
    descricao: "",
    cpfPagador: "",
    nomePagador: "",
    emailPagador: "",
    enderRua: "",
    enderBairro: "",
    enderCEP: "",
    enderCidade: "",
    enderComplemento: "",
    enderEstado: "AC",
    enderNumero: "",
    enderReferencia: "",
    permitirRegras: false,
    isCancelado: false,
    mesesPular: 0,
    dataPrimeiraEmissao: moment().format(dateForAPI),
    numeroPagador: "",
    isAvulso: false,
    dataEmissao: "",
    enviarEmail: true,
    enviarSMS: false,
    enviarWhats: false,
    RegrasJuros: { mode: "SEM_JUROS", frequency: null, value: 0, start_at: "" },
    RegrasMulta: { mode: "SEM_MULTA", value: 0, start_at: "" },
    RegrasDesconto: { mode: "SEM_DESCONTO", value: 0, diasAntesVencimento: 0 },
    descontoNaPrimeiraParcela: false,
  };

  const [boleto, setBoleto] = useState<VendaBoleto>(initialValues);
  const [numBoletos, setNumBoletos] = useState<number>(2);
  const [batchResponses, setBatchResponses] = useState<any[]>([]);
  const [batchObjects, setBatchObjects] = useState<VendaBoleto[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [validation, setValidation] = useState<boolean>(false);
  const [sameMonth, setSameMonth] = useState<boolean>(false);
  const [monthsToSkip, setMonthsToSkip] = useState<number>(0);

  function onChangeValorFixo(e: any) {
    setBoleto({
      ...boleto,
      valor: Number(e.target.value.slice(3).replace(",", ".")),
    });
  }

  function onChangeValorPrimeiraEmissao(e: any) {
    setBoleto({
      ...boleto,
      valorPrimeiraEmissao: Number(e.target.value.slice(3).replace(",", ".")),
    });
  }

  useEffect(() => {
    setBoleto({ ...boleto, mesesPular: sameMonth ? -1 : monthsToSkip });
  }, [sameMonth, monthsToSkip]);

  async function handleBatchCreation() {
    setLoadingState(true);
    try {
      const interestConfig = getInterestConfig(
        boleto.RegrasJuros?.mode || "SEM_JUROS"
      );
      const multaConfig = getMultaConfig(boleto.RegrasMulta.mode);
      const lotePayload = {
        idCliente: cliente,
        idLogin: idLogin,
        diaVencimento: boleto.diaVencimento,
        parcelas: numBoletos,
        isCancelado: false,
        dataPrimeiraEmissao: moment(boleto.dataPrimeiraEmissao).format(
          dateForAPI
        ),
        mensagem: boleto.descricao,
        qtdMesesPular: boleto.mesesPular,
        descontoNaPrimeiraParcela: boleto.descontoNaPrimeiraParcela,
        numeroPagador: unMask(boleto.numeroPagador),
        valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
        enviarEmail: boleto.enviarEmail,
        enviarSMS: boleto.enviarSMS,
        enviarWhats: boleto.enviarWhats,
        boletoBase: {
          idLogin: idLogin,
          idCliente: cliente,
          dataVencimento: moment(boleto.dataPrimeiraEmissao)
            .date(boleto.diaVencimento)
            .format(dateForAPI),
          dataEmissao: moment().format(dateForAPI),
          isAvulso: false,
          valor: Number(boleto.valor).toFixed(2),
          valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
          mensagem: boleto.descricao,
          cpfPagador: unMask(boleto.cpfPagador),
          nomePagador: boleto.nomePagador,
          emailPagador: boleto.emailPagador,
          enderRua: boleto.enderRua,
          enderBairro: boleto.enderBairro,
          enderCEP: unMask(boleto.enderCEP),
          enderCidade: boleto.enderCidade,
          enderComplemento: boleto.enderComplemento,
          enderEstado: boleto.enderEstado,
          enderNumero: boleto.enderNumero,
          enderReferencia: boleto.enderReferencia,
          permitirRegras: boleto.permitirRegras,
          enviarEmail: boleto.enviarEmail,
          enviarSMS: boleto.enviarSMS,
          enviarWhats: boleto.enviarWhats,
          RegrasJuros: {
            mode:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.mode
                : "SEM_JUROS",
            frequency:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.frequency
                : null,
            value:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? convertValueToCentsString(
                  boleto.RegrasJuros?.value || 0,
                  interestConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasMulta: {
            mode:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? multaConfig.mode
                : "SEM_MULTA",
            value:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? convertValueToCentsString(
                  boleto.RegrasMulta.value,
                  multaConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasDesconto: {
            mode:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? boleto.RegrasDesconto.mode
                : "SEM_DESCONTO",
            value:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? convertValueToCentsString(
                  boleto.RegrasDesconto.value,
                  boleto.RegrasDesconto.mode === "PERCENTAGE"
                )
                : "0",
            diasAntesVencimento:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? Number(boleto.RegrasDesconto.diasAntesVencimento)
                : 0,
          },
        },
      };
      const response = await VendasBoletoLoteApi.postVendaBoletoLote(
        lotePayload
      );
      setBatchResponses(response.boletos);
      toast.success("Lote de boletos criado com sucesso!");
      return response;
    } catch (error: any) {
      toast.error(`Erro ao criar lote: ${extrairMensagemErro(error)}`);
      return undefined;
    } finally {
      setLoadingState(false);
    }
  }

  async function onSubmit() {
    const valorMinimo = boleto.permitirRegras ? 10 : 5;
    if (
      boleto.valor < valorMinimo ||
      boleto.valorPrimeiraEmissao < valorMinimo
    ) {
      toast.error(
        "Os valores dos boletos devem ser maiores ou iguais ao mínimo!"
      );
      return;
    }

    const cpfLength = unMask(boleto.cpfPagador).length;
    if (cpfLength < 11 || cpfLength > 14) {
      toast.error("O CPF/CNPJ deve ter entre 11 e 14 dígitos.");
      return;
    }
    if (unMask(boleto.enderCEP).length !== 8) {
      toast.error("O CEP deve ter exatamente 8 dígitos.");
      return;
    }
    if (unMask(boleto.numeroPagador).length < 10) {
      toast.error("O número de telefone deve ter pelo menos 10 dígitos.");
      return;
    }
    if (numBoletos < 2 || numBoletos > 99999) {
      toast.error("O número de boletos deve ser entre 2 e 99999.");
      return;
    }
    if (
      moment(boleto.dataPrimeiraEmissao).isSame(moment(), "day") &&
      Number(moment().format("HH.mm")) > 17
    ) {
      toast.error(
        "Não é possível realizar solicitações para o mesmo dia após as 16hs!"
      );
      return;
    }
    if (boleto.mesesPular < -1) {
      toast.error("O número de meses a pular não pode ser menor que -1.");
      return;
    }
    if (boleto.diaVencimento < 1 || boleto.diaVencimento > 31) {
      toast.error("O dia de vencimento deve estar entre 1 e 31.");
      return;
    }

    const erroRegra =
      getRegraJurosErro() || getRegraMultaErro() || getRegraDescontoErro();
    if (erroRegra) {
      toast.error(erroRegra);
      return;
    }

    const erroDiaVenc = getDiaVencimentoErro();
    if (erroDiaVenc) {
      toast.error(erroDiaVenc);
      return;
    }

    setLoadingState(true);
    try {
      const interestConfig = getInterestConfig(
        boleto.RegrasJuros?.mode || "SEM_JUROS"
      );
      const multaConfig = getMultaConfig(boleto.RegrasMulta.mode);
      const lotePayload = {
        idCliente: cliente,
        idLogin: idLogin,
        diaVencimento: boleto.diaVencimento,
        parcelas: numBoletos,
        isCancelado: false,
        dataPrimeiraEmissao: moment(boleto.dataPrimeiraEmissao).format(
          dateForAPI
        ),
        mensagem: boleto.descricao,
        qtdMesesPular: boleto.mesesPular,
        descontoNaPrimeiraParcela: boleto.descontoNaPrimeiraParcela,
        numeroPagador: unMask(boleto.numeroPagador),
        valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
        enviarEmail: boleto.enviarEmail,
        enviarSMS: boleto.enviarSMS,
        enviarWhats: boleto.enviarWhats,
        boletoBase: {
          idLogin: idLogin,
          idCliente: cliente,
          dataVencimento: moment(boleto.dataPrimeiraEmissao)
            .date(boleto.diaVencimento)
            .format(dateForAPI),
          dataEmissao: moment().format(dateForAPI),
          isAvulso: false,
          valor: Number(boleto.valor).toFixed(2),
          valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
          mensagem: boleto.descricao,
          cpfPagador: unMask(boleto.cpfPagador),
          nomePagador: boleto.nomePagador,
          emailPagador: boleto.emailPagador,
          enderRua: boleto.enderRua,
          enderBairro: boleto.enderBairro,
          enderCEP: unMask(boleto.enderCEP),
          enderCidade: boleto.enderCidade,
          enderComplemento: boleto.enderComplemento,
          enderEstado: boleto.enderEstado,
          enderNumero: boleto.enderNumero,
          enderReferencia: boleto.enderReferencia,
          permitirRegras: boleto.permitirRegras,
          enviarEmail: boleto.enviarEmail,
          enviarSMS: boleto.enviarSMS,
          enviarWhats: boleto.enviarWhats,
          RegrasJuros: {
            mode:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.mode
                : "SEM_JUROS",
            frequency:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.frequency
                : null,
            value:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? convertValueToCentsString(
                  boleto.RegrasJuros?.value || 0,
                  interestConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasMulta: {
            mode:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? multaConfig.mode
                : "SEM_MULTA",
            value:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? convertValueToCentsString(
                  boleto.RegrasMulta.value,
                  multaConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasDesconto: {
            mode:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? boleto.RegrasDesconto.mode
                : "SEM_DESCONTO",
            value:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? convertValueToCentsString(
                  boleto.RegrasDesconto.value,
                  boleto.RegrasDesconto.mode === "PERCENTAGE"
                )
                : "0",
            diasAntesVencimento:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? Number(boleto.RegrasDesconto.diasAntesVencimento)
                : 0,
          },
        },
      };
      const response =
        await VendasBoletoLoteApi.postVendaBoletoLoteVisualizacao(lotePayload);
      const previewObjects: VendaBoleto[] = response.boletos.map(
        (item: any, index: number) => {
          const interestConfig = getInterestConfig(
            boleto.RegrasJuros?.mode || "SEM_JUROS"
          );
          const multaConfig = getMultaConfig(boleto.RegrasMulta.mode);
          return {
            ...boleto,
            valor: index === 0 ? boleto.valorPrimeiraEmissao : boleto.valor,
            vencimento: item.dataVencimento,
            dataLimitePagamento: item.dataLimitePagamento,
            dataPrimeiraEmissao:
              item.dataPrimeiraEmissao || boleto.dataPrimeiraEmissao,
            descricao: `${boleto.descricao} - Parcela ${index + 1}`,
            cpfPagador: unMask(boleto.cpfPagador),
            enderCEP: unMask(boleto.enderCEP),
            numeroPagador: unMask(boleto.numeroPagador),
            enviarEmail: boleto.enviarEmail,
            enviarSMS: boleto.enviarSMS,
            enviarWhats: boleto.enviarWhats,
            RegrasJuros: {
              mode:
                boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                  ? interestConfig.mode
                  : "SEM_JUROS",
              frequency:
                boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                  ? interestConfig.frequency
                  : null,
              value:
                boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                  ? convertValueToCentsString(
                    boleto.RegrasJuros?.value || 0,
                    interestConfig.isPercentage
                  )
                  : "0",
              start_at: item.dataVencimento,
            },
            RegrasMulta: {
              mode:
                boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                  ? multaConfig.mode
                  : "SEM_MULTA",
              value:
                boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                  ? convertValueToCentsString(
                    boleto.RegrasMulta.value,
                    multaConfig.isPercentage
                  )
                  : "0",
              start_at: item.dataVencimento,
            },
            RegrasDesconto: {
              mode:
                boleto.permitirRegras &&
                  boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
                  (index !== 0 || boleto.descontoNaPrimeiraParcela)
                  ? boleto.RegrasDesconto.mode
                  : "SEM_DESCONTO",
              value:
                boleto.permitirRegras &&
                  boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
                  (index !== 0 || boleto.descontoNaPrimeiraParcela)
                  ? convertValueToCentsString(
                    boleto.RegrasDesconto.value,
                    boleto.RegrasDesconto.mode === "PERCENTAGE"
                  )
                  : "0",
              diasAntesVencimento:
                boleto.permitirRegras &&
                  boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
                  (index !== 0 || boleto.descontoNaPrimeiraParcela)
                  ? Number(boleto.RegrasDesconto.diasAntesVencimento)
                  : 0,
            },
          };
        }
      );
      setBatchObjects(previewObjects);
      setIsOpen(true);
      // Modal aberta assume o controle; libera o botão da página.
      setLoadingState(false);
    } catch (error: any) {
      toast.error(`Erro ao obter visualização: ${extrairMensagemErro(error)}`);
      setLoadingState(false);
    }
  }

  function validateCPForCNPJ(item: string) {
    const temp = unMask(item);
    if (temp.length < 11 || temp.length > 14) {
      setValidation(true);
      return;
    }
    setValidation(
      temp.length === 11
        ? !cpfValidator.isValid(temp)
        : !cnpjValidator.isValid(temp)
    );
  }

  async function addressCep(cep: any) {
    try {
      if (unMask(cep).length >= 8) {
        const { data } = await axios.get(
          `https://viacep.com.br/ws/${cep}/json/`
        );
        if (data.erro) {
          toast.error("CEP não encontrado!");
          return;
        }
        setBoleto((prev) => ({
          ...prev,
          enderRua: data.logradouro,
          enderBairro: data.bairro,
          enderCEP: data.cep,
          enderCidade: data.localidade,
          enderComplemento: data.complemento,
          enderEstado: data.uf,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  function validarMesmoDiaEAntesDasDezesseisHoras(data: any) {
    const horaDaSolicitacao = moment().format("HH.mm");
    const diadaSolicitação =
      moment().format("DD-MM-YYYY") === moment(data).format("DD-MM-YYYY");
    return !diadaSolicitação || Number(horaDaSolicitacao) < 16;
  }

  const validarData = (e: any) => {
    if (validarMesmoDiaEAntesDasDezesseisHoras(e.target.value)) {
      setBoleto({
        ...boleto,
        dataPrimeiraEmissao: moment(e.target.value).format(dateForAPI),
      });
    } else {
      toast.error(
        "Não é possível realizar solicitações para o mesmo dia após as 16hs!"
      );
    }
  };

  function validaData(item: any) {
    if (moment(item) < moment().startOf("day"))
      toast.error("A data não pode ser anterior ao dia de hoje!");
  }

  // Entrada com valor diferente das demais parcelas: um valor FIXO em R$ que
  // serve para a mensalidade não serve para a entrada — o teto legal muda de
  // tamanho junto com a parcela. Percentual não tem esse problema: 1% de R$ 10
  // e 1% de R$ 100 saem certos sozinhos. Por isso, com valores divergentes só
  // percentual é oferecido.
  const valoresDivergentes =
    boleto.valorPrimeiraEmissao > 0 &&
    boleto.valor > 0 &&
    boleto.valorPrimeiraEmissao !== boleto.valor;

  // `jurosPermitido` é calculado mais abaixo (depende de menorParcela); aqui só
  // interessa o limiar, que é constante.
  const parcelaComportaJuros =
    boleto.valorPrimeiraEmissao > 0 || boleto.valor > 0
      ? Math.min(
        ...[boleto.valorPrimeiraEmissao, boleto.valor].filter((v) => v > 0)
      ) >= VALOR_MINIMO_COM_JUROS
      : true;

  const OPTIONS_INTEREST = !parcelaComportaJuros
    ? [{ key: 1, value: "SEM_JUROS", text: "Sem juros" }]
    : [
      { key: 1, value: "SEM_JUROS", text: "Sem juros" },
      ...(valoresDivergentes
        ? []
        : [{ key: 2, value: "VALOR_DIARIO", text: "Valor diário" }]),
      { key: 3, value: "PERCENTUAL_DIARIO", text: "Percentual diário" },
      { key: 4, value: "PERCENTUAL_MENSAL", text: "Percentual mensal" },
    ];

  const OPTIONS_MULTA = [
    { key: 1, value: "SEM_MULTA", text: "Sem Multa" },
    ...(valoresDivergentes
      ? []
      : [{ key: 2, value: "VALOR_FIXO", text: "Valor Fixo" }]),
    { key: 3, value: "PERCENTUAL", text: "Valor em Porcentagem" },
  ];

  // Desconto: o percentual sempre existiu no back e na validação da core, e só
  // estava oculto aqui. Ele é liberado exatamente no caso em que o valor fixo
  // não serve — entrada e mensalidade diferentes.
  const OPTIONS_DESCONTO = valoresDivergentes
    ? [
      { key: 1, value: "SEM_DESCONTO", text: "Sem Desconto" },
      { key: 3, value: "PERCENTAGE", text: "Percentual" },
    ]
    : [
      { key: 1, value: "SEM_DESCONTO", text: "Sem Desconto" },
      { key: 2, value: "FIXED", text: "Valor Fixo" },
    ];

  // Limites legais (BACEN) — % real (o ×100 só ocorre no build do payload)
  const LIMITE_MULTA_PCT = 2;
  const LIMITE_JUROS_MENSAL_PCT = 1;
  const LIMITE_JUROS_DIARIO_PCT = 1 / 30;

  const fmtBRL = (n: number) =>
    (Number.isFinite(n) ? n : 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  // Mesmo piso usado no onSubmit: nenhum boleto pode ser emitido abaixo disso.
  const valorMinimoBoleto = boleto.permitirRegras ? 10 : 5;

  // Juros e multa incidem em TODAS as parcelas — inclusive na entrada, que
  // quase sempre é menor que a mensalidade. Calcular o teto legal só sobre a
  // mensalidade fazia o lote passar aqui e na core e estourar depois, na hora
  // de emitir a 1ª parcela. Base correta = a MENOR parcela do lote.
  const parcelasInformadas = [boleto.valorPrimeiraEmissao, boleto.valor].filter(
    (v) => v > 0
  );
  const menorParcela = parcelasInformadas.length
    ? Math.min(...parcelasInformadas)
    : 0;

  const tetoMultaFixa = menorParcela * (LIMITE_MULTA_PCT / 100);
  const tetoJurosDiaFixo = menorParcela * (LIMITE_JUROS_DIARIO_PCT / 100);
  // Juros só é possível a partir de R$ 30,00 por parcela. A multa não tem piso
  // (2% de R$ 10 = R$ 0,20, válido), então ela continua liberada.
  const jurosPermitido = menorParcela >= VALOR_MINIMO_COM_JUROS;

  // Desconto só toca a 1ª parcela quando o cliente habilita explicitamente —
  // então a base muda conforme o check. E ele não pode derrubar a parcela
  // abaixo do valor mínimo emitível.
  const baseDesconto = boleto.descontoNaPrimeiraParcela
    ? menorParcela
    : boleto.valor;
  const valorDesconto = Number(boleto.RegrasDesconto.value) || 0;
  const descontoEhPercentual = boleto.RegrasDesconto.mode === "PERCENTAGE";
  const fmtPct = (n: number) =>
    `${(Number.isFinite(n) ? n : 0)
      .toFixed(2)
      .replace(".", ",")
      .replace(/,00$/, "")}%`;

  // Quanto o desconto vale, em R$, sobre uma parcela específica. É aqui que o
  // percentual ganha do fixo: ele se ajusta sozinho ao valor de cada parcela.
  const descontoEmReais = (valorParcela: number) =>
    descontoEhPercentual
      ? (valorParcela * valorDesconto) / 100
      : valorDesconto;

  // Teto do campo, já na unidade em que ele é digitado (R$ ou %).
  const tetoParaBase = (base: number) => {
    if (base <= 0) return 0;
    const sobra = Math.max(base - valorMinimoBoleto, 0);
    return descontoEhPercentual ? (sobra / base) * 100 : sobra;
  };
  const tetoDesconto = tetoParaBase(baseDesconto);
  const tetoDescontoPrimeira = tetoParaBase(boleto.valorPrimeiraEmissao);
  const fmtTetoDesconto = (n: number) =>
    descontoEhPercentual ? fmtPct(n) : fmtBRL(n);

  const descontoAtivo =
    boleto.permitirRegras &&
    boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
    valorDesconto > 0;
  // "Aplicar na 1ª parcela" só é ofertado quando o desconto cabe na entrada.
  const descontoCabeNaPrimeira =
    boleto.valorPrimeiraEmissao > 0 && valorDesconto <= tetoDescontoPrimeira;

  const getRegraJurosErro = (): string | null => {
    const rj = boleto.RegrasJuros;
    if (!boleto.permitirRegras || !rj || rj.mode === "SEM_JUROS") return null;
    if (!jurosPermitido)
      return `A menor parcela (${fmtBRL(
        menorParcela
      )}) não admite juros: abaixo de ${fmtBRL(
        VALOR_MINIMO_COM_JUROS
      )} o mínimo de ${fmtBRL(PISO_JUROS_DIA)}/dia do banco já passa do teto legal de 1% ao mês.`;

    const v = Number(rj.value) || 0;
    if (v <= 0) return "Informe o valor do juros.";

    if (rj.mode === "PERCENTUAL_MENSAL") {
      if (v > LIMITE_JUROS_MENSAL_PCT)
        return "Juros mensal não pode exceder 1% (limite legal).";
      if ((menorParcela * v) / 100 < PISO_JUROS_MES)
        return `Na menor parcela (${fmtBRL(menorParcela)}) isso dá ${fmtBRL(
          (menorParcela * v) / 100
        )}/mês — abaixo do mínimo de ${fmtBRL(PISO_JUROS_MES)}/mês do banco.`;
    }
    if (rj.mode === "PERCENTUAL_DIARIO") {
      if (v > LIMITE_JUROS_DIARIO_PCT)
        return "Juros diário não pode exceder 0,033% (equivale a 1% ao mês).";
      if ((menorParcela * v) / 100 < PISO_JUROS_DIA)
        return `Na menor parcela (${fmtBRL(menorParcela)}) isso dá ${fmtBRL(
          (menorParcela * v) / 100
        )}/dia — abaixo do mínimo de ${fmtBRL(PISO_JUROS_DIA)}/dia do banco.`;
    }
    if (rj.mode === "VALOR_DIARIO") {
      if (v < PISO_JUROS_DIA)
        return `Juros diário fixo não pode ser menor que ${fmtBRL(
          PISO_JUROS_DIA
        )}/dia.`;
      if (v > tetoJurosDiaFixo)
        return `Juros diário fixo não pode exceder 0,033%/dia da menor parcela (${fmtBRL(
          menorParcela
        )}) — máx. ${fmtBRL(tetoJurosDiaFixo)}/dia.`;
    }
    return null;
  };

  const getRegraMultaErro = (): string | null => {
    const rm = boleto.RegrasMulta;
    if (!boleto.permitirRegras || !rm || rm.mode === "SEM_MULTA") return null;
    const v = Number(rm.value) || 0;
    if (v <= 0) return "Informe o valor da multa.";
    if (rm.mode === "PERCENTUAL" && v > LIMITE_MULTA_PCT)
      return "Multa não pode exceder 2% (limite legal).";
    if (rm.mode === "VALOR_FIXO" && v > tetoMultaFixa)
      return `Multa fixa não pode exceder 2% da menor parcela (${fmtBRL(
        menorParcela
      )}) — máx. ${fmtBRL(tetoMultaFixa)}.`;
    return null;
  };

  const getRegraDescontoErro = (): string | null => {
    const rd = boleto.RegrasDesconto;
    if (!boleto.permitirRegras || !rd || rd.mode === "SEM_DESCONTO") return null;
    const v = Number(rd.value) || 0;
    if (v <= 0) return "Informe o valor do desconto.";
    if (descontoEhPercentual && v >= 100)
      return "O desconto percentual não pode ser igual ou maior que 100%.";
    if (!descontoEhPercentual && v >= baseDesconto)
      return `Desconto não pode ser igual ou maior que a parcela em que ele incide (${fmtBRL(
        baseDesconto
      )}).`;
    if (v > tetoDesconto)
      return `Com este desconto a parcela de ${fmtBRL(
        baseDesconto
      )} ficaria abaixo do mínimo de ${fmtBRL(
        valorMinimoBoleto
      )}. Máximo: ${fmtTetoDesconto(tetoDesconto)}.`;
    if (Number(rd.diasAntesVencimento) < 0)
      return "Os dias de antecipação não podem ser negativos.";
    return null;
  };

  // Valores passaram a divergir com uma regra em valor fixo já escolhida: troca
  // para o percentual equivalente e zera o campo (a unidade mudou). O contrário
  // também vale — o desconto percentual só existe enquanto houver divergência,
  // senão o Select ficaria com um valor fora da lista.
  useEffect(() => {
    const jurosFixo = boleto.RegrasJuros?.mode === "VALOR_DIARIO";
    const multaFixa = boleto.RegrasMulta.mode === "VALOR_FIXO";
    const descontoFixo = boleto.RegrasDesconto.mode === "FIXED";
    const descontoPct = boleto.RegrasDesconto.mode === "PERCENTAGE";

    if (valoresDivergentes && (jurosFixo || multaFixa || descontoFixo)) {
      setBoleto((prev) => ({
        ...prev,
        RegrasJuros: jurosFixo
          ? {
            ...prev.RegrasJuros!,
            mode: "PERCENTUAL_DIARIO",
            frequency: "DAILY",
            value: 0,
          }
          : prev.RegrasJuros,
        RegrasMulta: multaFixa
          ? { ...prev.RegrasMulta, mode: "PERCENTUAL", value: 0 }
          : prev.RegrasMulta,
        RegrasDesconto: descontoFixo
          ? { ...prev.RegrasDesconto, mode: "PERCENTAGE", value: 0 }
          : prev.RegrasDesconto,
      }));
      toast.info(
        "A entrada tem valor diferente das demais parcelas: as regras passam a ser em percentual. Informe os valores novamente."
      );
      return;
    }

    if (!parcelaComportaJuros && boleto.RegrasJuros?.mode !== "SEM_JUROS") {
      setBoleto((prev) => ({
        ...prev,
        RegrasJuros: {
          ...prev.RegrasJuros!,
          mode: "SEM_JUROS",
          frequency: null,
          value: 0,
        },
      }));
      toast.info(
        `Parcelas abaixo de ${VALOR_MINIMO_COM_JUROS.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })} não admitem juros — o mínimo cobrado pelo banco já passaria do teto legal.`
      );
      return;
    }

    if (!valoresDivergentes && descontoPct) {
      setBoleto((prev) => ({
        ...prev,
        RegrasDesconto: { ...prev.RegrasDesconto, mode: "FIXED", value: 0 },
      }));
    }
  }, [
    valoresDivergentes,
    parcelaComportaJuros,
    boleto.RegrasJuros?.mode,
    boleto.RegrasMulta.mode,
    boleto.RegrasDesconto.mode,
  ]);

  // Se o desconto deixar de caber na entrada (valor da 1ª parcela mudou, ou o
  // desconto subiu), desmarca sozinho. Antes dava para criar o lote e só
  // descobrir o problema na falha de emissão da 1ª parcela.
  useEffect(() => {
    if (boleto.descontoNaPrimeiraParcela && !descontoCabeNaPrimeira) {
      setBoleto((prev) => ({ ...prev, descontoNaPrimeiraParcela: false }));
    }
  }, [descontoCabeNaPrimeira, boleto.descontoNaPrimeiraParcela]);

  // Mesmo mês: o dia de vencimento da 2ª parcela deve ser após o dia da 1ª emissão
  const getDiaVencimentoErro = (): string | null => {
    if (!sameMonth) return null;
    const diaPrimeira = moment(boleto.dataPrimeiraEmissao).date();
    if (boleto.diaVencimento <= diaPrimeira)
      return `No mesmo mês, o dia de vencimento (${boleto.diaVencimento}) deve ser maior que o dia da 1ª emissão (${diaPrimeira}).`;
    return null;
  };

  const isFormValid = () => {
    const cpfLength = unMask(boleto.cpfPagador).length;
    const cepLength = unMask(boleto.enderCEP).length;
    const numeroPagadorLength = unMask(boleto.numeroPagador).length;
    const hasNotification =
      boleto.enviarEmail || boleto.enviarSMS || boleto.enviarWhats;
    return (
      cliente > 0 &&
      boleto.valor > 0.01 &&
      boleto.valorPrimeiraEmissao > 0.01 &&
      cpfLength >= 11 &&
      cpfLength <= 14 &&
      boleto.emailPagador !== "" &&
      boleto.nomePagador !== "" &&
      cepLength === 8 &&
      boleto.enderRua !== "" &&
      boleto.enderNumero !== "" &&
      boleto.enderBairro !== "" &&
      boleto.enderCidade !== "" &&
      boleto.enderEstado !== "" &&
      numeroPagadorLength >= 10 &&
      numBoletos >= 2 &&
      numBoletos <= 99999 &&
      boleto.mesesPular >= -1 &&
      boleto.diaVencimento >= 1 &&
      boleto.diaVencimento <= 31 &&
      hasNotification &&
      !getRegraJurosErro() &&
      !getRegraMultaErro() &&
      !getRegraDescontoErro() &&
      !getDiaVencimentoErro()
    );
  };

  // Exemplo dinâmico do intervalo até a 2ª parcela
  const capitalizarMes = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1);
  const mesPrimeiraLabel = capitalizarMes(
    moment(boleto.dataPrimeiraEmissao).locale("pt-br").format("MMMM [de] YYYY")
  );
  const mesSegundaLabel = capitalizarMes(
    moment(boleto.dataPrimeiraEmissao)
      .locale("pt-br")
      .add(monthsToSkip + 1, "months")
      .format("MMMM [de] YYYY")
  );

  // ---------------------------------------------------------------- simulador
  // Espelha a geração de datas do core (BolepixController.CriarLote): a 1ª
  // parcela vence na data informada; as demais em dataPrimeiraEmissao +
  // (mesesPular + i) meses, no dia escolhido, truncado para o último dia do mês
  // quando o dia não existe (ex.: 31 em fevereiro).
  const qtdParcelas =
    Number.isFinite(numBoletos) && numBoletos > 0 ? numBoletos : 0;

  const vencimentoDaParcela = (i: number) => {
    const base = moment(boleto.dataPrimeiraEmissao);
    if (i === 0) return base;
    const alvo = base.clone().add(boleto.mesesPular + i, "months");
    return alvo.date(Math.min(boleto.diaVencimento, alvo.daysInMonth()));
  };

  const descontoDaParcela = (i: number, valor: number) => {
    if (!descontoAtivo) return 0;
    if (i === 0 && !boleto.descontoNaPrimeiraParcela) return 0;
    return Math.min(descontoEmReais(valor), valor);
  };

  const indicesSimulados = Array.from(
    new Set([0, 1, 2, qtdParcelas - 1].filter((i) => i >= 0 && i < qtdParcelas))
  ).sort((a, b) => a - b);

  const parcelasSimuladas = indicesSimulados.map((i) => {
    const vencimento = vencimentoDaParcela(i);
    const valor = i === 0 ? boleto.valorPrimeiraEmissao : boleto.valor;
    const desconto = descontoDaParcela(i, valor);
    return {
      indice: i,
      rotulo:
        i === 0
          ? "1ª (entrada)"
          : i === qtdParcelas - 1
            ? `${qtdParcelas}ª (última)`
            : `${i + 1}ª`,
      vencimento: vencimento.format("DD/MM/YYYY"),
      valor,
      desconto,
      valorComDesconto: valor - desconto,
      limiteDesconto:
        desconto > 0
          ? vencimento
              .clone()
              .subtract(
                Number(boleto.RegrasDesconto.diasAntesVencimento) || 0,
                "days"
              )
              .format("DD/MM/YYYY")
          : null,
    };
  });

  const totalLote =
    boleto.valorPrimeiraEmissao + boleto.valor * Math.max(qtdParcelas - 1, 0);
  const haParcelasOcultas = qtdParcelas > 4;
  const podeSimular =
    parcelasSimuladas.length > 0 &&
    (boleto.valorPrimeiraEmissao > 0 || boleto.valor > 0);

  return (
    <Flex
      w={"100%"}
      h={"90vh"}
      bgColor={colors.WHITE}
      p={{ base: 3, md: 5 }}
      shadow="lg"
      overflowY="auto"
      flexDir="column"
      maxW="1400px"
      mx="auto"
    >
      <Box mb={4}>
        <Flex alignItems="center" mb={2}>
          <FaPlay color={colors.BLUEMONEDDBUTTONS} size="20px" />
          <Heading as="h1" size="md" ml={3} color={colors.BLUEMONEDDBUTTONS}>
            Criar BOLETOS em LOTE
          </Heading>
        </Flex>
        <Divider borderColor="gray.200" />
      </Box>

      <Box mb={5}>
        <TitleFlex item="Informações de Pagamento" />
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={3}
          my={3}
          alignItems="start"
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>
                Valor 1ª Parcela (mín. {fmtBRL(valorMinimoBoleto)})
              </FormLabel>
              <NumericFormat
                {...campoSm}
                decimalScale={2}
                prefix="R$ "
                value={boleto.valorPrimeiraEmissao}
                decimalSeparator=","
                customInput={Input}
                placeholder="R$ 0,00"
                onChange={onChangeValorPrimeiraEmissao}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>
                Valor Demais Parcelas (mín. {fmtBRL(valorMinimoBoleto)})
              </FormLabel>
              <NumericFormat
                {...campoSm}
                decimalScale={2}
                prefix="R$ "
                value={boleto.valor}
                decimalSeparator=","
                customInput={Input}
                placeholder="R$ 0,00"
                onChange={onChangeValorFixo}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Vencimento do 1º Boleto</FormLabel>
              <Input
                {...campoSm}
                type="date"
                value={moment(boleto.dataPrimeiraEmissao).format(
                  dateForValueInput
                )}
                min={moment().startOf("day").format(dateForValueInput)}
                onChange={validarData}
                onBlur={(e) => validaData(e.target.value)}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Dia de Vencimento (1 a 31)</FormLabel>
              <NumberInput
                {...campoSm}
                value={boleto.diaVencimento}
                min={1}
                max={31}
                onChange={(v) =>
                  setBoleto({ ...boleto, diaVencimento: parseInt(v) })
                }
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText {...ajudaSm}>
                Dia do mês das demais parcelas (ajustado se o mês não tiver).
              </FormHelperText>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Quantidade de Parcelas</FormLabel>
              <NumberInput
                {...campoSm}
                value={numBoletos}
                min={2}
                max={99999}
                onChange={(v) => setNumBoletos(parseInt(v))}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 1, lg: 3 }}>
            <FormControl>
              <FormLabel {...labelSm}>Descrição Base</FormLabel>
              <Input
                {...campoSm}
                placeholder="Opcional (ex: Mensalidade Semestre 1)"
                value={boleto.descricao}
                onChange={(e) =>
                  setBoleto({ ...boleto, descricao: e.target.value })
                }
              />
              <FormHelperText {...ajudaSm}>
                Cada boleto terá " - Parcela X" anexado à descrição.
              </FormHelperText>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, md: 2, lg: 4 }}>
            <Flex
              gap={4}
              align="flex-start"
              flexDir={{ base: "column", md: "row" }}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
            >
              <FormControl isRequired flex="1">
                <FormLabel {...labelSm}>
                  Emitir a 2ª parcela no mesmo mês da 1ª?
                </FormLabel>
                <Switch
                  size="sm"
                  isChecked={sameMonth}
                  onChange={(e) => setSameMonth(e.target.checked)}
                />
                {sameMonth &&
                  (getDiaVencimentoErro() ? (
                    <Text mt={1} fontSize="xs" color="red.500" fontWeight="medium">
                      {getDiaVencimentoErro()}
                    </Text>
                  ) : (
                    <Text mt={1} fontSize="xs" color="green.500" fontWeight="medium">
                      OK — 2ª parcela em{" "}
                      {moment(boleto.dataPrimeiraEmissao)
                        .date(boleto.diaVencimento)
                        .format("DD/MM/YYYY")}
                      .
                    </Text>
                  ))}
                {!sameMonth && (
                  <FormHelperText {...ajudaSm}>
                    Se sim, o dia de vencimento deve ser após o dia da 1ª
                    emissão.
                  </FormHelperText>
                )}
              </FormControl>

              {!sameMonth && (
                <FormControl isRequired flex="1">
                  <FormLabel {...labelSm}>Meses a pular até a 2ª parcela</FormLabel>
                  <NumberInput
                    {...campoSm}
                    maxW="140px"
                    value={monthsToSkip}
                    min={0}
                    max={99999}
                    onChange={(v) => setMonthsToSkip(parseInt(v) || 0)}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText {...ajudaSm}>
                    <b>0</b> = mês seguinte · <b>1</b> = pula 1 mês · <b>2</b> =
                    pula 2 meses…
                  </FormHelperText>
                  <Text
                    mt={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    color={colors.BLUEMONEDDBUTTONS}
                  >
                    1ª em {mesPrimeiraLabel} → 2ª em {mesSegundaLabel}.
                  </Text>
                </FormControl>
              )}
            </Flex>
          </GridItem>
        </Grid>

        {/* Simulação em tempo real das parcelas */}
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          bg="gray.50"
          p={3}
        >
          <Flex
            justify="space-between"
            align="baseline"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <Text fontWeight="bold" fontSize="sm">
              Simulação das parcelas
            </Text>
            <Text fontSize="xs" color="gray.600">
              {qtdParcelas > 0 ? `${qtdParcelas} parcelas · ` : ""}
              total {fmtBRL(totalLote)}
            </Text>
          </Flex>
          {!podeSimular ? (
            <Text fontSize="xs" color="gray.500">
              Informe os valores, a data e a quantidade de parcelas para ver a
              simulação.
            </Text>
          ) : (
            <Grid
              templateColumns={{
                base: "repeat(2, 1fr)",
                md: "repeat(auto-fit, minmax(150px, 1fr))",
              }}
              gap={2}
            >
              {parcelasSimuladas.map((p, idx) => (
                <Fragment key={p.indice}>
                  {haParcelasOcultas && idx === parcelasSimuladas.length - 1 && (
                    <Flex
                      align="center"
                      justify="center"
                      color="gray.400"
                      fontWeight="bold"
                      display={{ base: "none", md: "flex" }}
                    >
                      ···
                    </Flex>
                  )}
                  <Box
                    bg={colors.WHITE}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={3}
                    py={2}
                  >
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                      {p.rotulo}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      vence {p.vencimento}
                    </Text>
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={colors.BLUEMONEDDBUTTONS}
                    >
                      {fmtBRL(p.valor)}
                    </Text>
                    {p.desconto > 0 && (
                      <Text fontSize="xs" color="green.600">
                        {fmtBRL(p.valorComDesconto)} até {p.limiteDesconto}
                      </Text>
                    )}
                  </Box>
                </Fragment>
              ))}
            </Grid>
          )}
          {boleto.permitirRegras &&
            (boleto.RegrasJuros?.mode !== "SEM_JUROS" ||
              boleto.RegrasMulta.mode !== "SEM_MULTA") && (
              <Text fontSize="xs" color="gray.600" mt={2}>
                Valores sem atraso — juros e multa só entram após o vencimento
                de cada parcela.
              </Text>
            )}
        </Box>
      </Box>

      <Box mb={5}>
        <Flex alignItems="center" mb={3}>
          <Switch
            size="sm"
            mr={3}
            isChecked={boleto.permitirRegras}
            onChange={() =>
              setBoleto({
                ...boleto,
                permitirRegras: !boleto.permitirRegras,
                RegrasJuros: {
                  mode: "SEM_JUROS",
                  frequency: null,
                  value: 0,
                  start_at: boleto.vencimento,
                },
                RegrasMulta: {
                  mode: "SEM_MULTA",
                  value: 0,
                  start_at: boleto.vencimento,
                },
                RegrasDesconto: {
                  mode: "SEM_DESCONTO",
                  value: 0,
                  diasAntesVencimento: 0,
                },
                descontoNaPrimeiraParcela: false,
              })
            }
          />
          <Text fontWeight="medium" fontSize="sm">
            Aplicar Regras de Juros, Multa e Desconto
          </Text>
        </Flex>
        {boleto.permitirRegras && (
          <>
            <TitleFlex item="Regras do Boleto" />

            <Alert
              status="info"
              variant="left-accent"
              borderRadius="md"
              mb={3}
              py={2}
              alignItems="flex-start"
            >
              <AlertIcon boxSize="14px" mt="3px" />
              <Box fontSize="xs">
                <Text>
                  Os limites abaixo são calculados sobre a{" "}
                  <b>menor parcela do lote</b> ({fmtBRL(menorParcela)}), porque
                  juros e multa incidem em <b>todas</b> as parcelas — inclusive
                  na entrada.
                </Text>
                <Text color="gray.600">
                  Multa máx. 2% · juros entre {fmtBRL(PISO_JUROS_DIA)}/dia
                  (mínimo do banco) e 0,033%/dia ou 1%/mês (teto legal).
                </Text>
                {!parcelaComportaJuros && menorParcela > 0 && (
                  <Text mt={1} color="orange.600">
                    A menor parcela ({fmtBRL(menorParcela)}) <b>não admite
                    juros</b>: abaixo de {fmtBRL(VALOR_MINIMO_COM_JUROS)} o
                    mínimo de {fmtBRL(PISO_JUROS_DIA)}/dia do banco já
                    ultrapassa o teto legal de 1% ao mês. Multa e desconto
                    continuam disponíveis.
                  </Text>
                )}
                {valoresDivergentes && (
                  <Text mt={1}>
                    A entrada ({fmtBRL(boleto.valorPrimeiraEmissao)}) tem valor
                    diferente das demais ({fmtBRL(boleto.valor)}), então as
                    regras só podem ser <b>percentuais</b> — assim cada parcela
                    recebe o valor proporcional a ela.
                  </Text>
                )}
              </Box>
            </Alert>

            {/* Bloco 1 — Juros e Multa */}
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              mb={3}
            >
              <Text fontWeight="bold" fontSize="sm" mb={2}>
                Juros e Multa
              </Text>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                }}
                gap={3}
                alignItems="start"
              >
                <GridItem>
                  <FormControl>
                    <FormLabel {...labelSm}>Regras de Juros</FormLabel>
                    <Select
                      {...campoSm}
                      value={boleto.RegrasJuros?.mode || "SEM_JUROS"}
                      onChange={(e) => {
                        const config = getInterestConfig(e.target.value);
                        setBoleto({
                          ...boleto,
                          RegrasJuros: {
                            mode: e.target.value,
                            frequency: config.frequency,
                            value: 0,
                            start_at: boleto.vencimento,
                          },
                        });
                      }}
                    >
                      {OPTIONS_INTEREST.map((item) => (
                        <option key={item.key} value={item.value}>
                          {item.text}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>

                <GridItem>
                  {boleto.RegrasJuros?.mode === "PERCENTUAL_DIARIO" && (
                    <FormControl isInvalid={!!getRegraJurosErro()}>
                      <FormLabel {...labelSm}>Percentual Diário (%)</FormLabel>
                      <NumericFormat
                        {...campoSm}
                        decimalSeparator=","
                        decimalScale={3}
                        customInput={Input}
                        value={boleto.RegrasJuros.value}
                        placeholder="0,000"
                        onValueChange={({ value }) => {
                          const num = parseFloat(value.replace(",", ".")) || 0;
                          setBoleto({
                            ...boleto,
                            RegrasJuros: { ...boleto.RegrasJuros!, value: num },
                          });
                        }}
                      />
                      <FormErrorMessage {...ajudaSm}>
                        {getRegraJurosErro()}
                      </FormErrorMessage>
                      <FormHelperText {...ajudaSm}>
                        Máx. 0,033% ao dia.
                      </FormHelperText>
                    </FormControl>
                  )}

                  {boleto.RegrasJuros?.mode === "PERCENTUAL_MENSAL" && (
                    <FormControl isInvalid={!!getRegraJurosErro()}>
                      <FormLabel {...labelSm}>Percentual Mensal (%)</FormLabel>
                      <NumericFormat
                        {...campoSm}
                        decimalSeparator=","
                        decimalScale={2}
                        customInput={Input}
                        value={boleto.RegrasJuros.value}
                        placeholder="0,00"
                        onValueChange={({ value }) => {
                          const num = parseFloat(value.replace(",", ".")) || 0;
                          setBoleto({
                            ...boleto,
                            RegrasJuros: { ...boleto.RegrasJuros!, value: num },
                          });
                        }}
                      />
                      <FormErrorMessage {...ajudaSm}>
                        {getRegraJurosErro()}
                      </FormErrorMessage>
                      <FormHelperText {...ajudaSm}>
                        Máx. 1% ao mês.
                      </FormHelperText>
                    </FormControl>
                  )}

                  {boleto.RegrasJuros?.mode === "VALOR_DIARIO" && (
                    <FormControl isInvalid={!!getRegraJurosErro()}>
                      <FormLabel {...labelSm}>Valor Diário (R$)</FormLabel>
                      <NumericFormat
                        {...campoSm}
                        decimalSeparator=","
                        decimalScale={2}
                        customInput={Input}
                        value={boleto.RegrasJuros.value}
                        placeholder="0,00"
                        onValueChange={({ value }) => {
                          const num = parseFloat(value.replace(",", ".")) || 0;
                          setBoleto({
                            ...boleto,
                            RegrasJuros: { ...boleto.RegrasJuros!, value: num },
                          });
                        }}
                      />
                      <FormErrorMessage {...ajudaSm}>
                        {getRegraJurosErro()}
                      </FormErrorMessage>
                      <FormHelperText {...ajudaSm}>
                        Entre {fmtBRL(PISO_JUROS_DIA)} e{" "}
                        {fmtBRL(tetoJurosDiaFixo)} por dia (0,033%/dia da menor
                        parcela).
                      </FormHelperText>
                    </FormControl>
                  )}
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel {...labelSm}>Regra de Multa</FormLabel>
                    <Select
                      {...campoSm}
                      value={boleto.RegrasMulta.mode}
                      onChange={(e) =>
                        setBoleto({
                          ...boleto,
                          RegrasMulta: {
                            ...boleto.RegrasMulta,
                            mode: e.target.value,
                            value: 0,
                            start_at: boleto.vencimento,
                          },
                        })
                      }
                    >
                      {OPTIONS_MULTA.map((item) => (
                        <option key={item.key} value={item.value}>
                          {item.text}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>

                <GridItem>
                  {boleto.RegrasMulta.mode === "VALOR_FIXO" && (
                    <FormControl isInvalid={!!getRegraMultaErro()}>
                      <FormLabel {...labelSm}>Valor Fixo (R$)</FormLabel>
                      <NumericFormat
                        {...campoSm}
                        decimalSeparator=","
                        decimalScale={2}
                        customInput={Input}
                        value={boleto.RegrasMulta.value}
                        placeholder="0,00"
                        onValueChange={({ value }) => {
                          const num = parseFloat(value.replace(",", ".")) || 0;
                          setBoleto({
                            ...boleto,
                            RegrasMulta: { ...boleto.RegrasMulta, value: num },
                          });
                        }}
                      />
                      <FormErrorMessage {...ajudaSm}>
                        {getRegraMultaErro()}
                      </FormErrorMessage>
                      <FormHelperText {...ajudaSm}>
                        Máx. {fmtBRL(tetoMultaFixa)} (2% da menor parcela).
                      </FormHelperText>
                    </FormControl>
                  )}

                  {boleto.RegrasMulta.mode === "PERCENTUAL" && (
                    <FormControl isInvalid={!!getRegraMultaErro()}>
                      <FormLabel {...labelSm}>Percentual (%)</FormLabel>
                      <NumericFormat
                        {...campoSm}
                        decimalSeparator=","
                        decimalScale={2}
                        customInput={Input}
                        value={boleto.RegrasMulta.value}
                        placeholder="0,00"
                        onValueChange={({ value }) => {
                          const num = parseFloat(value.replace(",", ".")) || 0;
                          setBoleto({
                            ...boleto,
                            RegrasMulta: { ...boleto.RegrasMulta, value: num },
                          });
                        }}
                      />
                      <FormErrorMessage {...ajudaSm}>
                        {getRegraMultaErro()}
                      </FormErrorMessage>
                      <FormHelperText {...ajudaSm}>
                        Máx. 2% do valor do boleto.
                      </FormHelperText>
                    </FormControl>
                  )}
                </GridItem>
              </Grid>
            </Box>

            {/* Bloco 2 — Desconto */}
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              mb={3}
            >
              <Text fontWeight="bold" fontSize="sm" mb={2}>
                Desconto por Antecipação
              </Text>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                }}
                gap={3}
                alignItems="start"
              >
                <GridItem>
                  <FormControl>
                    <FormLabel {...labelSm}>Tipo de desconto</FormLabel>
                    <Select
                      {...campoSm}
                      value={boleto.RegrasDesconto.mode}
                      onChange={(e) =>
                        setBoleto({
                          ...boleto,
                          RegrasDesconto: {
                            ...boleto.RegrasDesconto,
                            mode: e.target.value,
                            value: 0,
                            diasAntesVencimento: 0,
                          },
                          descontoNaPrimeiraParcela:
                            e.target.value === "SEM_DESCONTO"
                              ? false
                              : boleto.descontoNaPrimeiraParcela,
                        })
                      }
                    >
                      {OPTIONS_DESCONTO.map((item) => (
                        <option key={item.key} value={item.value}>
                          {item.text}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>

                {boleto.RegrasDesconto.mode !== "SEM_DESCONTO" && (
                  <>
                    <GridItem>
                      <FormControl isInvalid={!!getRegraDescontoErro()}>
                        <FormLabel {...labelSm}>
                          {descontoEhPercentual
                            ? "Percentual do desconto (%)"
                            : "Valor do desconto (R$)"}
                        </FormLabel>
                        <NumericFormat
                          {...campoSm}
                          decimalSeparator=","
                          decimalScale={2}
                          customInput={Input}
                          value={boleto.RegrasDesconto.value}
                          placeholder="0,00"
                          onValueChange={({ value }) => {
                            const num = parseFloat(value.replace(",", ".")) || 0;
                            setBoleto({
                              ...boleto,
                              RegrasDesconto: {
                                ...boleto.RegrasDesconto,
                                value: num,
                              },
                            });
                          }}
                        />
                        <FormErrorMessage {...ajudaSm}>
                          {getRegraDescontoErro()}
                        </FormErrorMessage>
                        <FormHelperText {...ajudaSm}>
                          {descontoEhPercentual
                            ? `Abatido de cada parcela — ${fmtPct(
                              valorDesconto
                            )} da parcela. Máx. ${fmtTetoDesconto(
                              tetoDesconto
                            )}.`
                            : `Abatido de cada parcela. Máx. ${fmtTetoDesconto(
                              tetoDesconto
                            )}.`}
                        </FormHelperText>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!getRegraDescontoErro()}>
                        <FormLabel {...labelSm}>Dias antes do vencimento</FormLabel>
                        <NumberInput
                          {...campoSm}
                          min={0}
                          value={boleto.RegrasDesconto.diasAntesVencimento}
                          onChange={(_, valueNumber) =>
                            setBoleto({
                              ...boleto,
                              RegrasDesconto: {
                                ...boleto.RegrasDesconto,
                                diasAntesVencimento: Number.isNaN(valueNumber)
                                  ? 0
                                  : valueNumber,
                              },
                            })
                          }
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <FormHelperText {...ajudaSm}>
                          Vale se pago até N dias antes de cada vencimento (0 =
                          até o vencimento).
                        </FormHelperText>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl>
                        <FormLabel {...labelSm}>1ª parcela (entrada)</FormLabel>
                        <Checkbox
                          size="sm"
                          isChecked={boleto.descontoNaPrimeiraParcela}
                          isDisabled={!descontoCabeNaPrimeira}
                          onChange={(e) =>
                            setBoleto({
                              ...boleto,
                              descontoNaPrimeiraParcela: e.target.checked,
                            })
                          }
                        >
                          Aplicar desconto também na 1ª parcela
                        </Checkbox>
                        <FormHelperText {...ajudaSm}>
                          {descontoCabeNaPrimeira
                            ? `A entrada de ${fmtBRL(
                                boleto.valorPrimeiraEmissao
                              )} ficaria em ${fmtBRL(
                                boleto.valorPrimeiraEmissao -
                                  descontoEmReais(boleto.valorPrimeiraEmissao)
                              )}.`
                            : `Indisponível: com desconto de ${fmtTetoDesconto(
                                valorDesconto
                              )} a entrada de ${fmtBRL(
                                boleto.valorPrimeiraEmissao
                              )} ficaria abaixo do mínimo de ${fmtBRL(
                                valorMinimoBoleto
                              )} (máx. ${fmtTetoDesconto(
                                tetoDescontoPrimeira
                              )}).`}
                        </FormHelperText>
                      </FormControl>
                    </GridItem>
                  </>
                )}
              </Grid>
            </Box>
          </>
        )}
      </Box>

      <Box mb={5}>
        <TitleFlex item="Informações do Pagador" />
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={3}
          my={3}
        >
          <GridItem>
            <FormControl isRequired isInvalid={validation}>
              <FormLabel {...labelSm}>CPF/CNPJ do Pagador</FormLabel>
              <PatternFormat
                {...campoSm}
                customInput={Input}
                format={
                  unMask(boleto.cpfPagador).length > 11
                    ? "##.###.###/####-##"
                    : "###.###.###-###"
                }
                placeholder="Digite o CPF ou CNPJ"
                value={boleto.cpfPagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, cpfPagador: e.target.value })
                }
                onBlur={(e) => validateCPForCNPJ(e.target.value)}
              />
              {validation && (
                <FormHelperText {...ajudaSm} color={colors.REDMONEDD}>
                  Digite um CPF ou CNPJ válido!
                </FormHelperText>
              )}
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Nome do Pagador</FormLabel>
              <Input
                {...campoSm}
                placeholder="Digite o nome"
                value={boleto.nomePagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, nomePagador: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>E-mail</FormLabel>
              <Input
                {...campoSm}
                type="email"
                placeholder="E-mail do Pagador"
                value={boleto.emailPagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, emailPagador: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Número do Pagador</FormLabel>
              <PatternFormat
                {...campoSm}
                customInput={Input}
                format="(##) #####-####"
                placeholder="(51) 99999-9999"
                value={boleto.numeroPagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, numeroPagador: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
        </Grid>

        <Flex align="center" gap={2} mt={3}>
          <FaEnvelope color={colors.BLUEMONEDDBUTTONS} size="14px" />
          <Text fontSize="sm">Notificação por e-mail</Text>
          <Switch size="sm" isChecked={true} isReadOnly />
          <Text fontSize="xs" color="gray.500">
            enviada ao pagador a cada emissão
          </Text>
        </Flex>
        {/* SMS e WhatsApp desativados por ora:
            boleto.enviarSMS / boleto.enviarWhats seguem no payload como false. */}
      </Box>

      <Box mb={5}>
        <TitleFlex item="Endereço de Cobrança" />
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={3}
          my={3}
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>CEP</FormLabel>
              <PatternFormat
                {...campoSm}
                customInput={Input}
                format="#####-###"
                value={boleto.enderCEP}
                onChange={(e) => {
                  setBoleto({ ...boleto, enderCEP: e.target.value });
                  addressCep(e.target.value);
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 1, lg: 2 }}>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Endereço</FormLabel>
              <Input
                {...campoSm}
                value={boleto.enderRua}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderRua: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Número</FormLabel>
              <Input
                {...campoSm}
                value={boleto.enderNumero}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderNumero: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel {...labelSm}>Complemento</FormLabel>
              <Input
                {...campoSm}
                value={boleto.enderComplemento}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderComplemento: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel {...labelSm}>Referência</FormLabel>
              <Input
                {...campoSm}
                value={boleto.enderReferencia}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderReferencia: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Bairro</FormLabel>
              <Input
                {...campoSm}
                value={boleto.enderBairro}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderBairro: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Cidade</FormLabel>
              <Input
                {...campoSm}
                value={boleto.enderCidade}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderCidade: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...labelSm}>Estado</FormLabel>
              <Select
                {...campoSm}
                value={boleto.enderEstado}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderEstado: e.target.value })
                }
              >
                {stateOptions.map((item, index) => (
                  <option value={item.value} key={index}>
                    {item.text}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      <Flex
        justifyContent="space-between"
        w="100%"
        mt={4}
        mb={2}
        flexDir={{ base: "column", md: "row" }}
        gap={3}
      >
        <Button
          size="sm"
          bgColor={colors.GRAY_DARK}
          color={colors.WHITE}
          w={{ base: "100%", md: "160px" }}
          onClick={() => goToVendaBolePixListagem(navigate)}
        >
          Cancelar
        </Button>
        <Button
          size="sm"
          bgColor={colors.BLUEMONEDDBUTTONS}
          color={colors.WHITE}
          isDisabled={!isFormValid() || loadingState}
          w={{ base: "100%", md: "260px" }}
          onClick={async () => {
            setLoadingState(true);
            await onSubmit();
          }}
        >
          {loadingState ? (
            <Spinner size="sm" />
          ) : (
            `Criar ${numBoletos} Boleto${numBoletos > 1 ? "s" : ""}`
          )}
        </Button>
      </Flex>

      <ModalRevisãoVendaBoletoLote
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        batchObjects={batchObjects}
        setBatchObjects={setBatchObjects}
        setBatchResponses={setBatchResponses}
        setBoleto={setBoleto}
        initialValues={initialValues}
        setLoadingState={setLoadingState}
        numBoletos={numBoletos}
        cliente={cliente}
        idLogin={idLogin}
        handleBatchCreation={handleBatchCreation}
      />
    </Flex>
  );
}
